import React, { useRef } from "react";
import type { ImageFieldValue } from "@bcc/core-renderers";
import type { ContentTypeField, ContentTypeFieldCondition } from "../API/Models";
import ImagePickerInput from "./ImagePickerInput";
import FilePickerInput from "./FilePickerInput";
import TagsInput from "./TagsInput";
import EmbeddedComponentsEditor from "./PageEditor/EmbeddedComponentsEditor";
import FieldInfoTip from "./FieldInfoTip";
import { getFieldUsageNote } from "./fieldUsageNotes";
import { resolveStorageUrl } from "../Helper/storage";
import { withDocumentAutoTitle } from "../Helper/documentAutoTitle";
import { FiCalendar, FiUser } from "react-icons/fi";

/** True when a text field is an "author"/"auteur" field : it then gets a « Mon nom » quick-fill button. */
const isAuthorField = (field: ContentTypeField): boolean => {
	const hay = `${field.key ?? ""} ${field.title ?? ""}`.toLowerCase();
	return hay.includes("auteur") || hay.includes("author");
};

export const normalizeFieldKey = (value?: string | null) =>
	(value ?? "")
		.trim()
		.replace(/[^a-zA-Z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

export const normalizeFieldKeyLookup = (value?: string | null) => normalizeFieldKey(value).toLowerCase();

const normalizeFieldType = (value?: string | null) => (value ?? "").trim().toLowerCase();

/** Implicit section for grouped fields left without a Group (mirrors FieldGroups.Informations). */
export const IMPLICIT_INFORMATIONS_GROUP = "Informations principales";
/** Tags/classification fields are forced into this section, rendered last (mirrors FieldGroups.Classement). */
export const CLASSEMENT_GROUP = "Classement";

export interface FieldFormEntry {
	field: ContentTypeField;
	index: number;
}

export interface FieldFormSection {
	label: string;
	entries: FieldFormEntry[];
}

/**
 * Splits a content type's fields into ordered form sections.
 *
 * When at least one visible field carries a `group`, sections are group-driven: fields render under
 * their group's header in declaration order, section order follows each group's first occurrence,
 * ungrouped fields fall into an implicit leading "Informations principales" section, and tags fields
 * are pulled into a "Classement" section forced last. When NO visible field has a group, the legacy
 * partition is returned unchanged (required-first, then optional, tags last) so stored/ungrouped
 * types render exactly as before. Pure : no React, safe to unit test.
 */
export function groupContentFields(
	fields: ReadonlyArray<ContentTypeField> | null | undefined
): { grouped: boolean; sections: FieldFormSection[] } {
	const entries: FieldFormEntry[] = (fields ?? []).map((field, index) => ({ field, index }));
	const visible = entries.filter(({ field }) => field && !field.hideInEditor);
	const isTags = ({ field }: FieldFormEntry) => normalizeFieldType(field.type) === "tags";

	const hasGroup = visible.some(({ field }) => (field.group ?? "").trim().length > 0);
	if (!hasGroup) {
		const tags = visible.filter(isTags);
		const nonTags = visible.filter((entry) => !isTags(entry));
		const required = nonTags.filter(({ field }) => field.condition?.required);
		const optional = [...nonTags.filter(({ field }) => !field.condition?.required), ...tags];
		return {
			grouped: false,
			sections: [
				{ label: "", entries: required },
				{ label: "Champs optionnels", entries: optional }
			]
		};
	}

	const order: string[] = [];
	const byGroup = new Map<string, FieldFormEntry[]>();
	const push = (label: string, entry: FieldFormEntry) => {
		let bucket = byGroup.get(label);
		if (!bucket) {
			bucket = [];
			byGroup.set(label, bucket);
			order.push(label);
		}
		bucket.push(entry);
	};

	for (const entry of visible) {
		if (isTags(entry)) {
			push(CLASSEMENT_GROUP, entry);
			continue;
		}
		const group = (entry.field.group ?? "").trim();
		push(group.length > 0 ? group : IMPLICIT_INFORMATIONS_GROUP, entry);
	}

	// Classement always closes the form, whatever its first-occurrence position.
	const ordered = [
		...order.filter((label) => label !== CLASSEMENT_GROUP),
		...order.filter((label) => label === CLASSEMENT_GROUP)
	];
	return {
		grouped: true,
		sections: ordered.map((label) => ({ label, entries: byGroup.get(label) ?? [] }))
	};
}

const getNumberValue = (raw: any): number | null => {
	if (raw == null || raw === "") return null;
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
};

const isEmptyValue = (value: any) => {
	if (value == null) return true;
	if (typeof value === "string") return value.trim().length === 0;
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === "object") return Object.keys(value).length === 0;
	return false;
};

const resolveImagePreview = (value?: ImageFieldValue | null) => {
	if (!value) return { src: "", center: null };
	if (typeof value === "string") return { src: value, center: null };
	return { src: value.src ?? "", center: value.options?.center ?? null };
};

const clampNumber = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getCenterPercent = (center?: { enabled?: boolean; x?: number; y?: number } | null) => {
	if (!center?.enabled) return null;
	const x = typeof center.x === "number" && Number.isFinite(center.x) ? center.x : 0;
	const y = typeof center.y === "number" && Number.isFinite(center.y) ? center.y : 0;
	return {
		x: (clampNumber(x, -1, 1) + 1) * 50,
		y: (clampNumber(y, -1, 1) + 1) * 50
	};
};

const toDisplayDate = (value: any) => {
	if (value == null) return "";
	const raw = String(value).trim();
	if (!raw) return "";
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
	if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw.replace(/-/g, "/");
	if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
		const datePart = raw.slice(0, 10);
		const [year, month, day] = datePart.split("-");
		return `${day}/${month}/${year}`;
	}
	return raw;
};

const normalizeDateInput = (value: string) => {
	const raw = value.trim();
	if (!raw) return "";
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
	if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw.replace(/-/g, "/");
	if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
		const [year, month, day] = raw.split("-");
		return `${day}/${month}/${year}`;
	}
	return raw;
};

const toPickerDate = (value: any) => {
	const raw = toDisplayDate(value);
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
		const [day, month, year] = raw.split("/");
		return `${year}-${month}-${day}`;
	}
	if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
	return "";
};

const buildTodayValue = () => {
	const now = new Date();
	const day = String(now.getDate()).padStart(2, "0");
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const year = String(now.getFullYear());
	return `${day}/${month}/${year}`;
};

const resolveFieldKey = (field: ContentTypeField, index: number) =>
	normalizeFieldKey(field.key ?? field.title ?? `field-${index}`);

/** Clé du champ « Titre » du type de contenu, ou null s'il n'en déclare pas. */
const findTitleFieldKey = (fields: ContentTypeField[]): string | null =>
	fields
		.map((field, index) => ({ field, key: resolveFieldKey(field, index) }))
		.find(({ field }) => normalizeFieldKeyLookup(field.key ?? field.title ?? "") === "title")?.key ?? null;

/**
 * « Moderne doux » input chrome, shared by every field branch (and by TagsInput /
 * FilePickerInput): larger hit target, rounded-xl, brand-blue focus ring, hover border.
 * `extra` carries the per-usage sizing so variants (mono textarea) don't fight the default.
 */
const INPUT_BASE =
	"w-full rounded-xl border bg-white shadow-sm transition hover:border-slate-300 focus:border-[#005CA9] focus:outline-none focus:ring-2 focus:ring-[#005CA9]/15 disabled:cursor-not-allowed disabled:bg-slate-50";
export const inputClass = (error?: string | null, extra = "px-3.5 py-3 text-[15px] text-slate-800") =>
	`${INPUT_BASE} ${error ? "border-rose-300" : "border-slate-200"} ${extra}`;

/**
 * Grid width: long-form fields (blocks editor, textareas, file picker with its preview,
 * recursive lists) span both columns; every short input pairs two per row on desktop so
 * the form stays compact. Mobile is always a single column.
 */
export const isWideField = (field: ContentTypeField) => {
	if (isBodyComponentsField(field)) return true;
	const type = normalizeFieldType(field.type);
	return type === "longtext" || type === "textarea" || type === "richtext" || type === "components" || type === "file" || type === "list";
};

/**
 * A `body` longtext field edits through the blocks editor (components array), never a raw
 * textarea : the body-to-components migration made components the canonical body shape.
 * Other longtext fields keep the plain textarea.
 */
export const isBodyComponentsField = (field: ContentTypeField) => {
	const type = normalizeFieldType(field.type);
	if (type !== "longtext" && type !== "textarea" && type !== "richtext") return false;
	return normalizeFieldKeyLookup(field.key ?? field.title ?? "") === "body";
};

/**
 * Legacy body adapter: a pre-migration HTML string becomes a single « Texte riche » block
 * carrying the HTML verbatim (the SDK renders that block byte-identically to the old string
 * path). An already-migrated array passes through untouched, anything empty becomes [].
 */
export const toBodyComponents = (raw: any): any[] => {
	if (Array.isArray(raw)) return raw;
	if (typeof raw === "string" && raw.trim()) {
		return [{ componentType: "RichText", content: { html: raw } }];
	}
	return [];
};

/**
 * Lazy migration at save time: any body field still holding an HTML string is converted to
 * its components shape, so EVERY save through the editor migrates the document : even when
 * the operator never touched the body. Returns the same object when nothing needs converting.
 */
export const adaptBodyFieldsForSave = (fields: ContentTypeField[] = [], values: Record<string, any>) => {
	let next: Record<string, any> | null = null;
	(fields ?? []).forEach((field, index) => {
		if (!isBodyComponentsField(field)) return;
		const key = resolveFieldKey(field, index);
		if (!key) return;
		const raw = (next ?? values)?.[key];
		if (typeof raw === "string" && raw.trim()) {
			next = { ...(next ?? values), [key]: toBodyComponents(raw) };
		}
	});
	return next ?? values;
};

export const hasAnyFieldValue = (fields: ContentTypeField[] = [], values: Record<string, any>) => {
	for (let index = 0; index < fields.length; index++) {
		const field = fields[index];
		// Hidden technical fields don't count as user input : they're carried along, not edited.
		if (field.hideInEditor) continue;
		const key = resolveFieldKey(field, index);
		if (!key) continue;
		if (!isEmptyValue(values?.[key])) return true;
	}
	return false;
};

export const validateContentItemValues = (fields: ContentTypeField[] = [], values: Record<string, any>) => {
	const errors: Record<string, string> = {};
	const keyCounts = new Map<string, number>();
	fields.forEach((field) => {
		if (field.hideInEditor) return;
		const keyLookup = normalizeFieldKeyLookup(field.key ?? field.title ?? "");
		if (!keyLookup) return;
		keyCounts.set(keyLookup, (keyCounts.get(keyLookup) ?? 0) + 1);
	});
	fields.forEach((field, index) => {
		// Fields hidden from the editor are never validated : their values pass through untouched.
		if (field.hideInEditor) return;
		const key = resolveFieldKey(field, index);
		const keyLookup = normalizeFieldKeyLookup(field.key ?? field.title ?? "");
		if (!key) return;
		if (keyLookup && keyCounts.get(keyLookup) && keyCounts.get(keyLookup)! > 1) {
			errors[key] = "Clé de champ dupliquée.";
		}
		const condition: ContentTypeFieldCondition | null | undefined = field.condition;
		const required = condition?.required ?? false;
		const type = normalizeFieldType(field.type);
		const value = values?.[key];

		if (required && isEmptyValue(value)) {
			errors[key] = "Ce champ est obligatoire.";
			return;
		}

		if (type === "text") {
			const length = condition?.length;
			const textValue = typeof value === "string" ? value : "";
			if (length?.minimum != null && textValue.length < length.minimum) {
				errors[key] = `Longueur minimale : ${length.minimum}.`;
			}
			if (length?.maximum != null && textValue.length > length.maximum) {
				errors[key] = `Longueur maximale : ${length.maximum}.`;
			}
		}

		if (type === "number" || type === "int" || type === "float") {
			const numberValue = getNumberValue(value);
			const range = condition?.number;
			if (numberValue != null && range?.minimum != null && numberValue < range.minimum) {
				errors[key] = `Valeur minimale : ${range.minimum}.`;
			}
			if (numberValue != null && range?.maximum != null && numberValue > range.maximum) {
				errors[key] = `Valeur maximale : ${range.maximum}.`;
			}
		}
	});

	return { errors, hasErrors: Object.keys(errors).length > 0 };
};

type FieldShellProps = {
	label?: string;
	required?: boolean;
	description?: string | null;
	/** Deep « comment le site utilise ce champ » note : rendered as an ⓘ hover tooltip. */
	usageNote?: string | null;
	error?: string | null;
	/** "label" for simple inputs (click-to-focus), "div" for composite widgets containing buttons. */
	as?: "label" | "div";
	/** Full-width field: spans both columns of the desktop grid (see isWideField). */
	wide?: boolean;
	children: React.ReactNode;
};

/**
 * Shared field chrome: the label row (with a discreet asterisk when the field is required and an
 * ⓘ usage tooltip for functional fields), the input itself, the config's help text, then the
 * validation error. Every field branch renders through it so descriptions and errors always land
 * in the same place.
 */
const FieldShell: React.FC<FieldShellProps> = ({
	label,
	required = false,
	description,
	usageNote,
	error,
	as = "div",
	wide = false,
	children
}) => {
	const content = (
		<>
			{label && (
				<span className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-600">
					<span>{label}</span>
					{required && (
						<span className="text-rose-500" title="Champ obligatoire" aria-hidden="true">
							*
						</span>
					)}
					<FieldInfoTip note={usageNote} />
				</span>
			)}
			{children}
			{description?.trim() ? <p className="text-xs leading-4 text-slate-500 mt-1">{description}</p> : null}
			{error && <p className="text-xs text-rose-600">{error}</p>}
		</>
	);
	const rootClass = `${wide ? "md:col-span-2 " : ""}space-y-1.5 min-w-0`;
	if (as === "label") return <label className={`${rootClass} block`}>{content}</label>;
	return <div className={rootClass}>{content}</div>;
};

type ContentItemFormProps = {
	fields: ContentTypeField[];
	value: Record<string, any>;
	onChange: (next: Record<string, any>) => void;
	disabled?: boolean;
	errors?: Record<string, string>;
	/** Content type key/id : feeds the tag-suggestion endpoint for "tags" fields. */
	contentTypeKey?: string;
	/** Live preview default for the embedded components editors: true on the full item page, false inside modals. */
	componentsPreviewDefault?: boolean;
	/** Local account display name : enables the « Mon nom » quick-fill on author fields when set. */
	authorQuickFillName?: string;
};

/** Dynamic input form for content fields based on the content type schema. */
const ContentItemForm: React.FC<ContentItemFormProps> = ({
	fields,
	value,
	onChange,
	disabled = false,
	errors = {},
	contentTypeKey,
	componentsPreviewDefault = true,
	authorQuickFillName
}) => {
	const dateInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
	if (!fields || fields.length === 0) {
		return <p className="text-sm text-slate-600">Aucun champ n'est défini pour ce type de contenu.</p>;
	}

	// Resolves the public thumbnail URL (+ focal point) for an image field value.
	const resolveThumb = (fieldValue: any): { src: string; centerPosition?: string } | null => {
		const preview = resolveImagePreview(fieldValue as ImageFieldValue | null);
		const previewSrc = preview.src ? resolveStorageUrl(preview.src) : "";
		if (!previewSrc) return null;
		const center = getCenterPercent(preview.center);
		return { src: previewSrc, centerPosition: center ? `${center.x}% ${center.y}%` : undefined };
	};

	// The image thumbnail + « Télécharger » block, shared by the inline and the
	// grouped (image-section) layouts.
	const renderThumb = (thumb: { src: string; centerPosition?: string }) => (
		<div className="flex flex-col items-start gap-2">
			<a
				href={thumb.src}
				target="_blank"
				rel="noreferrer"
				className="block relative h-40 w-40 overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
			>
				<img
					src={thumb.src}
					alt=""
					className="h-full w-full object-cover"
					style={thumb.centerPosition ? { objectPosition: thumb.centerPosition } : undefined}
				/>
			</a>
			<a
				href={thumb.src}
				download
				target="_blank"
				rel="noreferrer"
				className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
			>
				Télécharger
			</a>
		</div>
	);

	const renderField = (field: ContentTypeField, index: number, opts: { hideThumb?: boolean } = {}) => {
		const label = field.title?.trim() || `Champ ${index + 1}`;
		const key = resolveFieldKey(field, index);
		const usageNote = getFieldUsageNote(field.key ?? field.title);
		const type = normalizeFieldType(field.type);
		const error = errors?.[key];
		const fieldValue = value?.[key];
		const required = field.condition?.required ?? false;
		const wide = isWideField(field);

		if (!key) {
			return (
				<div key={`field-${index}`} className="md:col-span-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
					Le titre du champ est obligatoire.
				</div>
			);
		}

		if (type === "boolean") {
			return (
				<FieldShell key={key} wide={wide} as="label" label={label} required={required} description={field.description} usageNote={usageNote} error={error}>
					<span className={`flex items-center gap-3 ${inputClass(error, "px-3.5 py-3 text-[15px] text-slate-700 cursor-pointer")}`}>
						<input
							type="checkbox"
							checked={Boolean(fieldValue)}
							onChange={(event) => onChange({ ...value, [key]: event.target.checked })}
							disabled={disabled}
						/>
						<span>{Boolean(fieldValue) ? "Oui" : "Non"}</span>
					</span>
				</FieldShell>
			);
		}

		if (type === "image") {
			const thumb = resolveThumb(fieldValue);
			// `hideThumb` is set by the grouped image section, which relocates the
			// thumbnail to the right of the stacked path + alt-text fields.
			const picker = (
				<ImagePickerInput
					value={fieldValue ?? ""}
					onChange={(next) => onChange({ ...value, [key]: next ?? "" })}
					disabled={disabled}
				/>
			);
			return (
				<FieldShell
					key={key}
					wide={wide && !opts.hideThumb}
					as="div"
					label={label}
					required={required}
					description={field.description}
					usageNote={usageNote}
					error={error}
				>
					{opts.hideThumb ? (
						picker
					) : (
						<div className="flex flex-wrap items-start gap-3">
							<div className="min-w-[240px] flex-1">{picker}</div>
							{thumb && renderThumb(thumb)}
						</div>
					)}
				</FieldShell>
			);
		}

		if (type === "file") {
			return (
				<FieldShell key={key} wide={wide} as="div" label={label} required={required} description={field.description} usageNote={usageNote} error={error}>
					<FilePickerInput
						value={fieldValue ?? null}
						onChange={(next, picked) =>
							onChange(
								withDocumentAutoTitle(
									{ ...value, [key]: next ?? null },
									findTitleFieldKey(fields),
									next ? picked : null
								)
							)
						}
						disabled={disabled}
						folder={field.folder ?? "publications"}
					/>
				</FieldShell>
			);
		}

		if (type === "tags") {
			const tagsValue = typeof fieldValue === "string" ? fieldValue : fieldValue == null ? "" : String(fieldValue);
			// A tags field always explains itself: fall back to a standard help text when
			// the content type's config doesn't provide one.
			const tagsDescription =
				field.description ??
				"Mots-clés facultatifs pour le classement et la recherche. Appuyez sur Entrée ou tapez une virgule pour ajouter un tag ; des suggestions issues des contenus existants apparaissent pendant la saisie.";
			return (
				<FieldShell key={key} wide={wide} as="div" label={label} required={required} description={tagsDescription} usageNote={usageNote} error={error}>
					<TagsInput
						value={tagsValue}
						onChange={(next) => onChange({ ...value, [key]: next })}
						contentTypeKey={contentTypeKey}
						fieldKey={field.key ?? key}
						disabled={disabled}
						hasError={Boolean(error)}
					/>
				</FieldShell>
			);
		}

		if (type === "components") {
			const componentsValue = Array.isArray(fieldValue) ? fieldValue : [];
			// EmbeddedComponentsEditor renders its own label header, so the shell only adds
			// the description and the error line here.
			return (
				<FieldShell key={key} wide={wide} as="div" description={field.description} error={error}>
					<EmbeddedComponentsEditor
						defaultShowPreview={componentsPreviewDefault}
						label={label}
						helper="Gérez les blocs de contenu réutilisables et prévisualisez ci-dessous."
						value={componentsValue}
						onChange={(next) => onChange({ ...value, [key]: next })}
						disabled={disabled}
					/>
				</FieldShell>
			);
		}

		if (isBodyComponentsField(field)) {
			// Canonical body editing: blocks editor over the components array. A legacy
			// HTML-string body is presented as a single « Texte riche » block; committing
			// any change (and every save, via adaptBodyFieldsForSave) persists the array.
			return (
				<FieldShell key={key} wide={wide} as="div" description={field.description} error={error}>
					<EmbeddedComponentsEditor
						defaultShowPreview={componentsPreviewDefault}
						label={label}
						helper="Composez le corps avec des blocs. Le HTML existant est repris tel quel dans un bloc « Texte riche »."
						value={toBodyComponents(fieldValue)}
						onChange={(next) => onChange({ ...value, [key]: next })}
						disabled={disabled}
					/>
				</FieldShell>
			);
		}

		if (type === "longtext" || type === "textarea" || type === "richtext") {
			// Long-form text content (non-body) : renders as a multi-line textarea.
			// Used for fields migrated from Drupal `body[und][0][value]`
			// (HTML strings that can be 50+ lines long).
			const txt = typeof fieldValue === "string" ? fieldValue : (fieldValue == null ? "" : String(fieldValue));
			return (
				<FieldShell key={key} wide={wide} as="label" label={label} required={required} description={field.description} usageNote={usageNote} error={error}>
					<textarea
						className={inputClass(error, "px-3.5 py-3 text-[13px] leading-relaxed font-mono text-slate-800")}
						rows={Math.min(20, Math.max(6, (txt.match(/\n/g)?.length ?? 0) + 3))}
						value={txt}
						onChange={(event) => onChange({ ...value, [key]: event.target.value })}
						disabled={disabled}
					/>
				</FieldShell>
			);
		}

		if (type === "select") {
			// Dropdown bound to field.options (defined in the content type config).
			// Each option carries { value, label }; the stored value is the `value`.
			const options = Array.isArray(field.options) ? field.options : [];
			const currentValue = typeof fieldValue === "string" ? fieldValue : (fieldValue == null ? "" : String(fieldValue));
			return (
				<FieldShell key={key} wide={wide} as="label" label={label} required={required} description={field.description} usageNote={usageNote} error={error}>
					<select
						className={inputClass(error)}
						value={currentValue}
						onChange={(event) => {
							const raw = event.target.value;
							onChange({ ...value, [key]: raw === "" ? null : raw });
						}}
						disabled={disabled}
					>
						<option value="">- Sélectionner -</option>
						{options.map((opt, i) => {
							const optValue = (opt?.value ?? "").toString();
							const optLabel = opt?.label?.trim() || optValue;
							return (
								<option key={`${key}-opt-${i}`} value={optValue}>
									{optLabel}
								</option>
							);
						})}
					</select>
				</FieldShell>
			);
		}

		if (type === "list") {
			// Recursive list: each row is itself a content-item form built from
			// field.itemFields. Add / remove a row preserves the order. The data
			// shape stored is an array of { key: value } objects matching
			// itemFields' resolved keys.
			const itemFields = Array.isArray(field.itemFields) ? field.itemFields : [];
			const rows = Array.isArray(fieldValue) ? fieldValue : [];
			const updateRows = (next: any[]) => onChange({ ...value, [key]: next });
			return (
				<FieldShell key={key} wide={wide} as="div" label={label} required={required} description={field.description} usageNote={usageNote} error={error}>
					<div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
						<div className="flex items-center justify-end">
							<button
								type="button"
								onClick={() => updateRows([...rows, {}])}
								disabled={disabled}
								className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
							>
								+ Ajouter
							</button>
						</div>
						{rows.length === 0 ? (
							<p className="text-xs text-slate-500">Aucune entrée. Cliquez sur « Ajouter » pour commencer.</p>
						) : (
							<div className="space-y-3">
								{rows.map((row: any, rowIdx: number) => (
									<div key={`${key}-row-${rowIdx}`} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">#{rowIdx + 1}</span>
											<button
												type="button"
												onClick={() => updateRows(rows.filter((_: any, i: number) => i !== rowIdx))}
												disabled={disabled}
												className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
											>
												Supprimer
											</button>
										</div>
										<ContentItemForm
											fields={itemFields}
											value={row && typeof row === "object" ? row : {}}
											onChange={(nextRow) => {
												const nextRows = rows.slice();
												nextRows[rowIdx] = nextRow;
												updateRows(nextRows);
											}}
											disabled={disabled}
											contentTypeKey={contentTypeKey}
											authorQuickFillName={authorQuickFillName}
										/>
									</div>
								))}
							</div>
						)}
					</div>
				</FieldShell>
			);
		}

		const inputType = type === "date" ? "text" : type === "number" || type === "int" || type === "float" ? "number" : "text";
		const step = type === "int" ? 1 : type === "float" ? "any" : undefined;
		const displayValue =
			type === "date"
				? toDisplayDate(fieldValue)
				: inputType === "number"
				? fieldValue ?? ""
				: typeof fieldValue === "string"
					? fieldValue
					: fieldValue ?? "";

		return (
			<FieldShell key={key} wide={wide} as="label" label={label} required={required} description={field.description} usageNote={usageNote} error={error}>
				{type === "date" ? (
					<div className="flex flex-wrap items-center gap-2">
						<input
							type="text"
							placeholder="JJ/MM/AAAA"
							inputMode="numeric"
							className={`flex-1 min-w-[160px] ${inputClass(error)}`}
							value={displayValue}
							onChange={(event) => {
								const raw = event.target.value;
								onChange({ ...value, [key]: normalizeDateInput(raw) });
							}}
							disabled={disabled}
						/>
						<input
							ref={(el) => {
								dateInputRefs.current[key] = el;
							}}
							type="date"
							className="sr-only"
							value={toPickerDate(displayValue)}
							onChange={(event) => {
								onChange({ ...value, [key]: normalizeDateInput(event.target.value) });
							}}
							disabled={disabled}
						/>
						<button
							type="button"
							onClick={() => {
								const input = dateInputRefs.current[key];
								if (!input || disabled) return;
								if (typeof (input as any).showPicker === "function") {
									(input as any).showPicker();
								} else {
									input.click();
								}
							}}
							disabled={disabled}
							className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							<FiCalendar className="h-3.5 w-3.5" /> Choisir
						</button>
						<button
							type="button"
							onClick={() => onChange({ ...value, [key]: buildTodayValue() })}
							disabled={disabled}
							className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3.5 py-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							Aujourd'hui
						</button>
					</div>
				) : (() => {
					const textInput = (
					<input
						type={inputType}
						step={step}
						placeholder={type === "date" ? "JJ/MM/AAAA" : undefined}
						inputMode={type === "date" ? "numeric" : undefined}
						className={inputClass(error)}
						value={displayValue}
						onChange={(event) => {
							const raw = event.target.value;
							const nextValue =
								inputType === "number"
									? raw === "" ? null : Number(raw)
									: type === "date"
										? normalizeDateInput(raw)
										: raw;
							onChange({ ...value, [key]: nextValue });
						}}
						disabled={disabled}
					/>
					);
					// Author fields get a « Mon nom » button stuck to the right that fills the local
					// account display name in one click.
					if (type !== "date" && inputType !== "number" && isAuthorField(field)) {
						return (
							<div className="flex items-stretch gap-2">
								<div className="flex-1">{textInput}</div>
								<button
									type="button"
									onClick={() => onChange({ ...value, [key]: authorQuickFillName ?? "" })}
									disabled={disabled || !authorQuickFillName}
									title="Insérer le nom de mon compte"
									className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
								>
									<FiUser className="h-3.5 w-3.5" /> Mon nom
								</button>
							</div>
						);
					}
					return textInput;
				})()}
			</FieldShell>
		);
	};

	// Compact image section: image path + alt text STACKED on the left, the
	// thumbnail (+ « Télécharger ») to the RIGHT of both : saves vertical space on
	// long documents. Used for any group that carries an image field.
	const renderImageSection = (section: { label: string; entries: { field: ContentTypeField; index: number }[] }) => {
		const imageEntries = section.entries.filter(({ field }) => normalizeFieldType(field.type) === "image");
		const thumbs = imageEntries
			.map(({ field, index }) => resolveThumb(value?.[resolveFieldKey(field, index)]))
			.filter((t): t is { src: string; centerPosition?: string } => !!t);
		return (
			// Default-row flex-wrap (NOT `sm:flex-row`): the image path + alt text stack on the left, the
			// thumbnail sits to their RIGHT, and it wraps below only when the row can't fit. Mirrors the
			// inline image layout above. Two earlier attempts failed silently : a comma inside
			// `sm:grid-cols-[minmax(0,1fr)_auto]` stopped Tailwind emitting the class, and `sm:flex-row`
			// lost the cascade to the base `flex-col` in the admin's dev stylesheet ordering.
			<div className="md:col-span-2 flex flex-wrap items-start gap-5" data-image-section>
				<div className="grid min-w-[280px] flex-1 grid-cols-1 gap-y-4">
					{section.entries.map(({ field, index }) => renderField(field, index, { hideThumb: true }))}
				</div>
				{thumbs.length > 0 && (
					<div className="flex shrink-0 flex-col gap-3">
						{thumbs.map((t, i) => <React.Fragment key={i}>{renderThumb(t)}</React.Fragment>)}
					</div>
				)}
			</div>
		);
	};

	// Hidden technical fields are never rendered : their values still travel untouched because
	// every onChange spreads the full `value` object. When fields declare groups the form renders
	// section-by-section (Image + alt together, "À la une"/"En avant" together …); ungrouped/stored
	// types fall back to the legacy required-first / optional / tags-last partition.
	const { grouped, sections } = groupContentFields(fields);

	if (!grouped) {
		const [requiredSection, optionalSection] = sections;
		const requiredEntries = requiredSection.entries;
		const optionalEntries = optionalSection.entries;
		return (
			// Desktop: two-column grid : short fields pair up, long-form fields span both columns
			// (FieldShell `wide`). The DOM order (required → optional → tags) is untouched; the
			// grid only changes placement. Mobile collapses to one column.
			<div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
				{requiredEntries.map(({ field, index }) => renderField(field, index))}
				{requiredEntries.length > 0 && optionalEntries.length > 0 && (
					<div className="md:col-span-2 border-t border-slate-200 pt-3">
						<p className="text-xs uppercase tracking-wider text-slate-400">Champs optionnels</p>
					</div>
				)}
				{optionalEntries.map(({ field, index }) => renderField(field, index))}
			</div>
		);
	}

	// Grouped: one titled section per group, each a full-width divider header + its fields laid out
	// on the same two-column grid. The first section skips the top border so the form never opens
	// with a stray line. Empty sections (all their fields hidden) are dropped.
	const visibleSections = sections.filter((section) => section.entries.length > 0);
	return (
		<div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
			{visibleSections.map((section, sectionIndex) => {
				const hasImage = section.entries.some(({ field }) => normalizeFieldType(field.type) === "image");
				return (
					<React.Fragment key={section.label || `section-${sectionIndex}`}>
						<div
							className={`md:col-span-2 ${sectionIndex === 0 ? "" : "border-t border-slate-200 pt-3"}`}
							data-field-group={section.label}
						>
							<p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
								{section.label}
							</p>
						</div>
						{hasImage
							? renderImageSection(section)
							: section.entries.map(({ field, index }) => renderField(field, index))}
					</React.Fragment>
				);
			})}
		</div>
	);
};

export default ContentItemForm;
